import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MapIcon from '@mui/icons-material/Map';
import SensorsIcon from '@mui/icons-material/Sensors';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { useAuth } from '../../contexts/AuthContext';
import SecurityIcon from '@mui/icons-material/Security';

const drawerWidth = 240;

interface SidebarProps {
  open: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, toggleSidebar }) => {
  const theme = useTheme();
  const location = useLocation();
  const { isAdmin, user } = useAuth(); // Ajoutez `user` pour accéder au rôle
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Navigation items
  const mainNavItems = [
    { text: t('navigation.dashboard'), icon: <DashboardIcon />, path: '/' },
    { text: t('navigation.sensorsMap'), icon: <MapIcon />, path: '/map' },
    { text: t('navigation.sensors'), icon: <SensorsIcon />, path: '/sensors' },
    { text: t('navigation.alerts'), icon: <NotificationsIcon />, path: '/alerts' },
    { text: t('navigation.suspensions'), icon: <PauseCircleOutlineIcon />, path: '/suspensions' },
  ];

  // Admin navigation items (only shown to admin users)
  const adminNavItems = [
    { text: t('navigation.users'), icon: <PeopleIcon />, path: '/users' },
    { text: t('navigation.settings'), icon: <SettingsIcon />, path: '/settings' },
  ];

  // Vérifiez si l'utilisateur a le rôle `ingecom` et ajoutez les éléments
  if (user?.role === 'ingecom') {
    adminNavItems.push({
      text: t('navigation.ingecomSettings'),
      icon: <SecurityIcon />,
      path: '/ingecom-settings',
    });
  }

  // Check if a path is active
  const isActive = (path: string) => location.pathname === path;

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={toggleSidebar}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={toggleSidebar}>
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {mainNavItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={isActive(item.path)}
              onClick={isMobile ? toggleSidebar : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {isAdmin && (
        <>
          <Divider />
          <List>
            {adminNavItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  selected={isActive(item.path)}
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <span>{t('app.title')}</span>
        <br />
        <span>{t('app.version')}</span>
      </Box>
    </Drawer>
  );
};

export default Sidebar;